import React, { useEffect, useState } from "react";
import api, { extractErrorMessage } from "@/lib/api";
import { Check, X, History } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("pt-BR") : "—");
const STATUS = {
  approved: { label: "Aprovada", cls: "chip-success" },
  denied: { label: "Negada", cls: "chip-admin" },
  pending: { label: "Pendente", cls: "chip-judge" },
};

export const AccessRequests = ({ onChanged }) => {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);
  const [denying, setDenying] = useState(null);
  const [reason, setReason] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    const { data } = await api.get("/admin/access-requests");
    setItems(data);
  };

  useEffect(() => {
    load();
  }, []);

  const decide = async (rid, approve) => {
    setBusy(rid);
    setError("");
    try {
      if (approve) await api.post(`/admin/access-requests/${rid}/approve`);
      else await api.post(`/admin/access-requests/${rid}/deny`, { reason });
      setDenying(null);
      setReason("");
      await load();
      await onChanged();
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const pending = items.filter((r) => r.status === "pending");
  const history = items.filter((r) => r.status !== "pending");

  return (
    <div data-testid="access-requests">
      {error && (
        <div className="text-sm text-[#C9A84C] border border-[#C9A84C]/30 bg-[#C9A84C]/10 px-3 py-2 mb-3">{error}</div>
      )}
      <div className="overline mb-3">{pending.length} solicitação(ões) aguardando decisão</div>
      <div className="space-y-3 mb-8">
        {pending.length === 0 && (
          <div className="card p-6 text-center text-[var(--text-secondary)]">Nenhuma solicitação pendente.</div>
        )}
        {pending.map((r) => (
          <div key={r.id} className="card p-4 border-l-4 border-[#FFCC00]" data-testid={`access-req-${r.id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="font-semibold">
                  {r.operator_name} <span className="text-xs text-[var(--text-secondary)]">({r.operator_email})</span>
                </div>
                <div className="text-sm mt-1">
                  pede acesso a <b className="text-[#C9A84C]">{r.competition_name}</b>
                </div>
                {r.message && <div className="text-xs text-[var(--text-secondary)] mt-1 italic">“{r.message}”</div>}
                <div className="text-[11px] text-[var(--text-secondary)] mt-1">Solicitado em {fmt(r.created_at)}</div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid={`access-approve-${r.id}`}
                  className="btn-primary inline-flex items-center gap-1 !py-2"
                  disabled={busy === r.id}
                  onClick={() => decide(r.id, true)}
                >
                  <Check size={14} /> APROVAR
                </button>
                <button
                  data-testid={`access-deny-${r.id}`}
                  className="btn-ghost inline-flex items-center gap-1 !text-[#C9A84C]"
                  disabled={busy === r.id}
                  onClick={() => (denying === r.id ? setDenying(null) : setDenying(r.id))}
                >
                  <X size={14} /> Negar
                </button>
              </div>
            </div>
            {denying === r.id && (
              <div className="mt-3 flex gap-2 flex-wrap" data-testid={`access-deny-form-${r.id}`}>
                <input
                  data-testid={`access-deny-reason-${r.id}`}
                  className="input-field flex-1 min-w-[220px]"
                  placeholder="Motivo (opcional, visível para a operadora)"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
                <button data-testid={`access-deny-confirm-${r.id}`} className="btn-ghost !text-[#C9A84C]" disabled={busy === r.id} onClick={() => decide(r.id, false)}>
                  Confirmar negação
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      <button data-testid="access-history-toggle" className="btn-ghost inline-flex items-center gap-2 mb-3" onClick={() => setShowHistory((v) => !v)}>
        <History size={14} /> {showHistory ? "Ocultar histórico" : `Histórico (${history.length})`}
      </button>
      {showHistory && (
        <div className="card overflow-x-auto" data-testid="access-history">
          <table className="table-dense">
            <thead>
              <tr>
                <th>Data</th>
                <th>Operadora</th>
                <th>Campeonato</th>
                <th>Decisão</th>
                <th>Por</th>
                <th>Motivo</th>
              </tr>
            </thead>
            <tbody>
              {history.map((r) => {
                const s = STATUS[r.status] || STATUS.pending;
                return (
                  <tr key={r.id}>
                    <td className="text-xs tabular-nums">{fmt(r.decided_at || r.created_at)}</td>
                    <td className="text-sm">{r.operator_name}</td>
                    <td className="text-sm">{r.competition_name}</td>
                    <td><span className={`chip ${s.cls}`}>{s.label}</span></td>
                    <td className="text-xs text-[var(--text-secondary)]">{r.decided_by_name || "—"}</td>
                    <td className="text-xs text-[var(--text-secondary)]">{r.reason || "—"}</td>
                  </tr>
                );
              })}
              {history.length === 0 && (
                <tr><td colSpan={6} className="text-center py-6 text-[var(--text-secondary)]">Sem histórico.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
